import { createContext, useContext, type ReactNode } from "react";
import { observer } from "mobx-react-lite";
import { AlertTriangle, Loader2 } from "lucide-react";
import { TermsOfServiceModal } from "@/components/auth/TermsOfServiceModal";
import { SignOutEscapeButton } from "@/components/auth/SignOutEscapeButton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/hooks/useAuthStore";
import { useUserStore } from "@/hooks/useUserStore";
import { NO_PROFILE_ROW_ERROR } from "@/stores/AuthStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";

/** True once an ancestor gate has taken responsibility for mounting the modal. */
const TosGateMounted = createContext(false);

/**
 * Shown instead of the app when the profile the gate reads never arrived, so
 * consent can be neither confirmed nor asked for.
 *
 * Retry re-runs `loadSession`, not `loadProfile`: hydration gives up BEFORE it
 * reads the profile on both of its own failures (`membership.error` and a
 * missing `user_profiles` row), so re-reading the profile alone would clear
 * this screen while leaving the store with no roles and no orgs. loadSession
 * redoes the whole thing, and its own `fetchProfileRow` re-read covers the
 * brand-new account whose row has not reached the JWT yet.
 *
 * A missing row is called out separately, because it is the one failure a retry
 * cannot fix by itself — the row is trigger-created and never made from the
 * app. Retry stays offered (a row that is merely late does arrive), but the copy
 * sends the user to support rather than round the loop forever.
 */
const ProfileUnavailable = observer(function ProfileUnavailable() {
  const { t } = useLanguage();
  const authStore = useAuthStore();
  const missingProfileRow = authStore.error === NO_PROFILE_ROW_ERROR;

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Alert variant="destructive" className="max-w-md" data-testid="tos-profile-unavailable">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>
          {missingProfileRow ? t.auth.tosProfileMissingTitle : t.auth.tosProfileUnavailableTitle}
        </AlertTitle>
        <AlertDescription>
          <p className="mb-3">
            {missingProfileRow ? t.auth.tosProfileMissingBody : t.auth.tosProfileUnavailableBody}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => void authStore.loadSession()}>
              {t.common.retry}
            </Button>
            <SignOutEscapeButton variant={missingProfileRow ? "destructive" : "default"} />
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
});

/**
 * Mounts the blocking consent modal over an authenticated surface.
 *
 * Every route guard wraps its children in this, because the guards do NOT
 * compose: `/admin/*` and `/tools/drawings` are mounted under
 * `AdminProtectedRoute` / `PermissionProtectedRoute`, neither of which renders
 * `ProtectedRoute`. A gate living only in `ProtectedRoute` therefore let a
 * platform admin — or anyone holding `VIEW_DRAWING_COMPARE_TOOL` — deep-link
 * straight past consent while the same account was gated on `/buildings`.
 *
 * Guards DO nest (`/buildings/:id/drawings` is a `PermissionProtectedRoute`
 * inside a `ProtectedRoute`), so the context flag keeps the outermost gate the
 * only mount rather than stacking two dialogs on top of each other.
 */
export const TosConsentGate = observer(function TosConsentGate({
  children,
}: {
  children: ReactNode;
}) {
  const authStore = useAuthStore();
  const userStore = useUserStore();
  const gatedByAncestor = useContext(TosGateMounted);

  if (gatedByAncestor) return <>{children}</>;

  const userId = authStore.userId;

  // Inert without a session: /create-organization mounts the gate outside
  // ProtectedRoute, so an unauthenticated visitor gets the page's own redirect.
  if (!userId) return <>{children}</>;

  // Fail closed — a profile that is not loaded is NOT evidence of consent.
  // `loadProfile` leaves it null on a query error and on a missing row, and
  // AuthStore's `membership.error` branch returns before calling it at all;
  // rendering children on any of those would waive the gate for exactly the
  // accounts it exists to catch. Loading is still a wait, not a denial:
  // `loadSession` sets `userId` before it reads the profile.
  if (!userStore.isProfileLoaded) {
    if (authStore.isLoading || userStore.isLoading) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }
    return <ProfileUnavailable />;
  }

  return (
    <TosGateMounted.Provider value={true}>
      {!userStore.hasAcceptedTos && <TermsOfServiceModal userId={userId} />}
      {children}
    </TosGateMounted.Provider>
  );
});
